// CalendarStatusBatcher.ts
// Batches multiple SaveToCalendarButton status checks into single API calls.
// Buttons that mount within a short window are collected, then a single batch
// request is sent for all of them. This avoids flooding the browser's connection
// pool and keeps the main thread responsive during page load.

export interface EventStatus {
    isSaved: boolean;
    isExcluded: boolean;
}

export interface CommitteeStatus {
    isSubscribed: boolean;
}

type EventResolver = (status: EventStatus) => void;
type CommitteeResolver = (status: CommitteeStatus) => void;

interface EventBatch { [guid: string]: EventResolver[]; }
interface CommitteeBatch { [id: string]: CommitteeResolver[]; }

var BATCH_DELAY_MS = 50; // collect requests for 50ms before sending

var pendingEventGuids: EventBatch = {};
var pendingCommitteeIds: CommitteeBatch = {};
var eventTimer: ReturnType<typeof setTimeout> | null = null;
var committeeTimer: ReturnType<typeof setTimeout> | null = null;
var eventXhr: XMLHttpRequest | null = null;
var committeeXhr: XMLHttpRequest | null = null;

function objectKeys(obj: { [key: string]: any }): string[] {
    var keys: string[] = [];
    for (var key in obj) {
        if (obj.hasOwnProperty(key)) {
            keys.push(key);
        }
    }
    return keys;
}

function resolveAll(resolvers: EventResolver[], status: EventStatus) {
    for (var i = 0; i < resolvers.length; i++) {
        resolvers[i](status);
    }
}

function resolveAllCommittee(resolvers: CommitteeResolver[], status: CommitteeStatus) {
    for (var i = 0; i < resolvers.length; i++) {
        resolvers[i](status);
    }
}

function flushEventBatch() {
    eventTimer = null;
    var batch = pendingEventGuids;
    pendingEventGuids = {};

    var guids = objectKeys(batch);
    if (guids.length === 0) return;

    // Cancel any previous in-flight event batch
    if (eventXhr) {
        eventXhr.abort();
    }

    var xhr = new XMLHttpRequest();
    eventXhr = xhr;

    xhr.open('POST', '/api/SavedEvents/BatchGetEventStatus', true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
            try {
                var data = JSON.parse(xhr.responseText);
                var events: Array<{ eventContentGuid: string; isSaved: boolean; isExcluded: boolean }> = data.events || [];
                var resultMap: { [key: string]: EventStatus } = {};
                for (var i = 0; i < events.length; i++) {
                    var e = events[i];
                    resultMap[e.eventContentGuid.toLowerCase()] = { isSaved: e.isSaved, isExcluded: e.isExcluded };
                }
                for (var j = 0; j < guids.length; j++) {
                    var guid = guids[j];
                    var status = resultMap[guid.toLowerCase()] || { isSaved: false, isExcluded: false };
                    resolveAll(batch[guid], status);
                }
            } catch (err) {
                console.error('Failed to parse event status response:', err);
                for (var k = 0; k < guids.length; k++) {
                    resolveAll(batch[guids[k]], { isSaved: false, isExcluded: false });
                }
            }
        } else {
            console.error('Failed to batch-check event status: HTTP ' + xhr.status);
            for (var k = 0; k < guids.length; k++) {
                resolveAll(batch[guids[k]], { isSaved: false, isExcluded: false });
            }
        }
    };

    xhr.onerror = function () {
        console.error('Failed to batch-check event status');
        for (var k = 0; k < guids.length; k++) {
            resolveAll(batch[guids[k]], { isSaved: false, isExcluded: false });
        }
    };

    xhr.send(JSON.stringify({ eventContentGuids: guids }));
}

function flushCommitteeBatch() {
    committeeTimer = null;
    var batch = pendingCommitteeIds;
    pendingCommitteeIds = {};

    var idKeys = objectKeys(batch);
    if (idKeys.length === 0) return;

    var ids: number[] = [];
    for (var n = 0; n < idKeys.length; n++) {
        ids.push(parseInt(idKeys[n], 10));
    }

    if (committeeXhr) {
        committeeXhr.abort();
    }

    var xhr = new XMLHttpRequest();
    committeeXhr = xhr;

    xhr.open('POST', '/api/SubscribedCommittees/BatchGetCommitteeStatus', true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
            try {
                var data = JSON.parse(xhr.responseText);
                var committees: Array<{ committeeId: number; isSubscribed: boolean }> = data.committees || [];
                var resultMap: { [key: string]: boolean } = {};
                for (var i = 0; i < committees.length; i++) {
                    resultMap[String(committees[i].committeeId)] = committees[i].isSubscribed;
                }
                for (var j = 0; j < idKeys.length; j++) {
                    var key = idKeys[j];
                    var isSubscribed = resultMap[key] != null ? resultMap[key] : false;
                    resolveAllCommittee(batch[key], { isSubscribed: isSubscribed });
                }
            } catch (err) {
                console.error('Failed to parse committee status response:', err);
                for (var k = 0; k < idKeys.length; k++) {
                    resolveAllCommittee(batch[idKeys[k]], { isSubscribed: false });
                }
            }
        } else {
            console.error('Failed to batch-check committee status: HTTP ' + xhr.status);
            for (var k = 0; k < idKeys.length; k++) {
                resolveAllCommittee(batch[idKeys[k]], { isSubscribed: false });
            }
        }
    };

    xhr.onerror = function () {
        console.error('Failed to batch-check committee status');
        for (var k = 0; k < idKeys.length; k++) {
            resolveAllCommittee(batch[idKeys[k]], { isSubscribed: false });
        }
    };

    xhr.send(JSON.stringify({ committeeIds: ids }));
}

export function getEventStatus(eventContentGuid: string): Promise<EventStatus> {
    return new Promise<EventStatus>(function (resolve) {
        var key = eventContentGuid.toLowerCase();
        if (!pendingEventGuids[key]) {
            pendingEventGuids[key] = [];
        }
        pendingEventGuids[key].push(resolve);

        if (eventTimer === null) {
            eventTimer = setTimeout(flushEventBatch, BATCH_DELAY_MS);
        }
    });
}

export function getCommitteeStatus(committeeId: number): Promise<CommitteeStatus> {
    return new Promise<CommitteeStatus>(function (resolve) {
        var key = String(committeeId);
        if (!pendingCommitteeIds[key]) {
            pendingCommitteeIds[key] = [];
        }
        pendingCommitteeIds[key].push(resolve);

        if (committeeTimer === null) {
            committeeTimer = setTimeout(flushCommitteeBatch, BATCH_DELAY_MS);
        }
    });
}

export function cancelPendingRequests() {
    if (eventXhr) {
        eventXhr.abort();
        eventXhr = null;
    }
    if (committeeXhr) {
        committeeXhr.abort();
        committeeXhr = null;
    }
}
